/**
 * What this browser remembers about a reader's library: what they opened,
 * and what they kept.
 *
 * **Browser-local, and that is the honest shape of the product, not a stub.**
 * There are no accounts (brief §18 defers authentication), so there is
 * nowhere else for either list to live. The mock says the same thing in the
 * interface — "This library lives in this browser" — rather than designing a
 * sign-in that would settle §18 by accident.
 *
 * Only ids and timestamps are stored. The khutbah itself is fetched from the
 * server as usual, so nothing here can go stale against a republished source,
 * and a reader's history never holds khutbah text.
 */

const HISTORY_KEY = 'mimbar.library.history';
const SAVED_KEY = 'mimbar.library.saved';

/** Enough to be useful, bounded so one reader's storage cannot grow forever. */
const HISTORY_LIMIT = 50;

export interface HistoryEntry {
  readonly khutbahId: string;
  /** ISO 8601, so the stored form is readable and sorts correctly. */
  readonly readAt: string;
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // Another tab is the same library.
  window.addEventListener('storage', onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onChange);
  };
}

function readRaw(key: string): unknown {
  try {
    const value = localStorage.getItem(key);
    return value === null ? null : (JSON.parse(value) as unknown);
  } catch {
    // Storage throws outright in some contexts, and a hand-edited value can
    // be any shape at all. Either way the honest answer is "nothing stored".
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or a blocked store. A library that cannot be written is a worse
    // experience, not a broken one — reading still works.
  }
}

/** Ids are opaque to this module, but they do end up in a URL. */
function isId(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-zA-Z-]{1,64}$/.test(value);
}

export function readHistory(): HistoryEntry[] {
  const raw = readRaw(HISTORY_KEY);
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): HistoryEntry[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (!isId(record.khutbahId) || typeof record.readAt !== 'string') return [];
    return [{ khutbahId: record.khutbahId, readAt: record.readAt }];
  });
}

/**
 * Records a reading, newest first and once per khutbah.
 *
 * Re-reading moves an entry rather than adding one: a history with the same
 * khutbah forty times is not a history, and the only question it has to
 * answer is "what was I reading?".
 */
export function recordRead(khutbahId: string, now: Date): void {
  if (!isId(khutbahId)) return;
  const without = readHistory().filter((entry) => entry.khutbahId !== khutbahId);
  write(HISTORY_KEY, [{ khutbahId, readAt: now.toISOString() }, ...without].slice(0, HISTORY_LIMIT));
  notify();
}

export function readSaved(): string[] {
  const raw = readRaw(SAVED_KEY);
  return Array.isArray(raw) ? raw.filter(isId) : [];
}

export function isSaved(khutbahId: string): boolean {
  return readSaved().includes(khutbahId);
}

export function toggleSaved(khutbahId: string): boolean {
  if (!isId(khutbahId)) return false;
  const saved = readSaved();
  const next = saved.includes(khutbahId)
    ? saved.filter((id) => id !== khutbahId)
    : [khutbahId, ...saved];
  write(SAVED_KEY, next);
  notify();
  return next.includes(khutbahId);
}

export function clearLibrary(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(SAVED_KEY);
  } catch {
    // As above: nothing to do, and nothing worth failing over.
  }
  notify();
}
